import { useEffect, useId, useState } from 'react';
import { api, qs } from '../lib/api';
import { useSesion } from '../lib/sesion';
import type { ClienteResumen } from '../lib/tipos';
import { Aviso } from './ui';
const normal = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
function Destacado({ texto, q }: { texto: string; q: string }) {
  const i = normal(texto).indexOf(normal(q));
  return i < 0 || !q ? <>{texto}</> : <>{texto.slice(0, i)}<mark>{texto.slice(i, i + q.length)}</mark>{texto.slice(i + q.length)}</>;
}
/** Solo selección registra un reciente; escribir nunca modifica el historial. */
export function SelectorCliente({ onSeleccion }: { onSeleccion: (c: ClienteResumen) => void }) {
  const { es } = useSesion();
  const id = useId();
  const [q, setQ] = useState('');
  const [recientes, setRecientes] = useState<ClienteResumen[]>([]);
  const [resultados, setResultados] = useState<ClienteResumen[]>([]);
  const [abierto, setAbierto] = useState(true);
  const [activo, setActivo] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    api.get<{ rows: ClienteResumen[] }>('/clients/recent', controller.signal)
      .then(r => setRecientes(r.rows)).catch(e => { if (!controller.signal.aborted) setError(e.message); });
    return () => controller.abort();
  }, []);
  useEffect(() => {
    setResultados([]); setActivo(-1); setCargando(false);
    if (q.trim().length < 2) return;
    const controller = new AbortController();
    setCargando(true);
    const timer = window.setTimeout(() => {
      api.get<{ rows: ClienteResumen[] }>(`/clients/search${qs({ q: q.trim(), limit: 10 })}`, controller.signal)
        .then(r => setResultados(r.rows)).catch(e => { if (!controller.signal.aborted) setError(e.message); })
        .finally(() => { if (!controller.signal.aborted) setCargando(false); });
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [q]);
  const lista = q.trim().length >= 2 ? resultados : q.trim() ? [] : recientes;
  async function elegir(c: ClienteResumen) {
    setError(null);
    try {
      await api.post(`/clients/${c.id}/recent`);
      setRecientes([c, ...recientes.filter(r => r.id !== c.id)].slice(0, 2));
      setAbierto(false); onSeleccion(c);
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo seleccionar el cliente.'); }
  }
  return <div className="pila">
    <input type="search" role="combobox" aria-label="Buscar cliente" aria-controls={id}
      aria-expanded={abierto} aria-autocomplete="list" aria-activedescendant={activo >= 0 ? `${id}-${activo}` : undefined}
      placeholder="Escriba al menos 2 caracteres: nombre, localidad o Id."
      value={q} onFocus={() => setAbierto(true)} onChange={e => { setQ(e.target.value); setAbierto(true); setError(null); }}
      onKeyDown={e => {
        if (e.key === 'Escape') { setAbierto(false); setActivo(-1); }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault(); setAbierto(true);
          setActivo(n => !lista.length ? -1 : n < 0 ? (e.key === 'ArrowDown' ? 0 : lista.length - 1)
            : (n + (e.key === 'ArrowDown' ? 1 : -1) + lista.length) % lista.length);
        }
        if (e.key === 'Enter' && activo >= 0 && lista[activo]) { e.preventDefault(); void elegir(lista[activo]!); }
      }} />
    {error && <Aviso tono="error">{error}</Aviso>}
    {abierto && <>
      {!q.trim() && <strong>Últimos buscados</strong>}
      <div id={id} role="listbox" aria-label="Clientes" className="lista-clientes">
        {lista.map((c, i) => <button type="button" role="option" aria-selected={activo === i}
          id={`${id}-${i}`} key={c.id} className="cliente-opcion" onClick={() => void elegir(c)}>
          <span><span className="nombre"><Destacado texto={c.commercialName} q={q.trim()} /></span><br />
            <span className="sutil"><Destacado texto={[c.city, c.province].filter(Boolean).join(' · ')} q={q.trim()} />
              {es('ADMIN', 'SALES_MANAGER') && c.salesperson && <> · <Destacado texto={c.salesperson.name ?? ''} q={q.trim()} /></>}
            </span></span><span className="codigo"><Destacado texto={c.externalId} q={q.trim()} /></span>
        </button>)}
      </div>
      <span role="status" className="sutil">{cargando ? 'Buscando…' : lista.length ? `${lista.length} ${lista.length === 1 ? 'cliente' : 'clientes'}` : q.trim().length >= 2 ? 'No hay coincidencias.' : q.trim() ? 'Escriba al menos 2 caracteres.' : 'Todavía no ha seleccionado clientes.'}</span>
    </>}
  </div>;
}
